import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, getAuthToken } from "../security/AuthLogin";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import AmountDropdown from "../components/amountDropdown";
import LoadingLottie from "../components/LoadingLottie";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faLink,
  faUser,
  faFileExcel,
  faSpinner,
  faCheck,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

import {
  fetchSoportesTecnicos,
  SoporteTecnico,
  FetchSoportesTecnicosParams,
} from "../api/soportesTecnicosApi";

import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";

/** Extiende para mensajes */
interface ISoporteTecnicoConMensajes extends SoporteTecnico {
  leido?: number;
  ultimo_emisor?: string | null;
  ultima_fecha_msg?: string | null;
  fecha_fin_ofertas?: string | null;

  /** NUEVO: estado que llega de favoritos (tipo=4) */
  estado_soporte_favorito?: string | null;
  dueno_nombre?: string | null;
}

/** Mapea estado a texto */
const mapEstado = (estado: string | null): string => {
  if (!estado) return "En estudio";
  switch (estado.toLowerCase()) {
    case "pendiente":
    case "en estudio":
      return "En estudio";
    case "valido":
      return "Viable";
    case "rechazado":
      return "No viable";
    case "oculto":
      return "Oculto";
    case "norecomendado":
      return "No recomendado";
    default:
      return estado.charAt(0).toUpperCase() + estado.slice(1);
  }
};

/** Prioridad para ordenar visualmente */
function getEmisorPriority(
  s: ISoporteTecnicoConMensajes,
  role: string
): number {
  const emisor = s.ultimo_emisor || "";
  if (!emisor) return 4;
  if (role === "cliente") {
    if (emisor === "nadie") return 1;
    if (emisor === "admin_tecnico") return 2;
    return 3;
  } else {
    if (emisor === "nadie") return 1;
    if (emisor === "cliente") return 2;
    return 3;
  }
}

/** Color de glow según mensajería / estado */
function getBackgroundColor(
  s: ISoporteTecnicoConMensajes,
  role: string
): string {
  // Admin/tecnico ve negro si oculto
  if (
    (role === "admin" || role === "tecnico") &&
    s.estado_soporte?.toLowerCase() === "oculto"
  ) {
    return "black";
  }
  const leido = Number(s.leido) || 0;
  const em = s.ultimo_emisor;
  if (role === "cliente") {
    if (em === "cliente") return "rgb(255,0,0)";
    if (em === "admin_tecnico") return leido === 0 ? "rgb(255,255,0)" : "white";
    return "rgb(255,0,0)";
  } else {
    if (em === "cliente" || em === "nadie") return "rgb(255,0,0)";
    if (em === "admin_tecnico") return "rgb(0,255,0)";
    return "white";
  }
}

const SoportesTecnicos: React.FC = () => {
  const { userRole } = useAuth();

  const [soportes, setSoportes] = useState<ISoporteTecnicoConMensajes[] | null>(
    null
  );
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"abiertos" | "cerrados">("abiertos");

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") || "1", 10);
  const limit = parseInt(searchParams.get("limit") || "10", 10);
  const maxPages = Math.ceil(total / limit);

  const [localSearch, setLocalSearch] = useState("");
  const [localFechaInicio, setLocalFechaInicio] = useState("");
  const [localFechaFin, setLocalFechaFin] = useState("");
  const [localImporteDesde, setLocalImporteDesde] = useState("");
  const [localImporteHasta, setLocalImporteHasta] = useState("");
  const [localAutor, setLocalAutor] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const [localAsignado, setLocalAsignado] = useState("");
  const [localNoRespondidos, setLocalNoRespondidos] = useState(false);

  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  function cleanNumberString(str: string) {
    return str.replace(/\./g, "").replace(",", ".");
  }

  // Actualiza param cerrados
  const updateCerradosParam = (mode: "abiertos" | "cerrados") => {
    const np = new URLSearchParams(searchParams);
    np.set("cerrados", mode === "cerrados" ? "true" : "false");
    np.set("page", "1");
    setSearchParams(np);
  };

  // Carga
  const load = async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctr = new AbortController();
    abortRef.current = ctr;
    setLoading(true);
    try {
      const params: FetchSoportesTecnicosParams = { page, limit };
      [
        "search",
        "fechaInicio",
        "fechaFin",
        "importeDesde",
        "importeHasta",
        "autor",
        "asignado",
        "cerrados",
        "noRespondidos",
      ].forEach((key) => {
        const v = searchParams.get(key);
        if (v) (params as any)[key] = v;
      });
      const resp = await fetchSoportesTecnicos(params, ctr.signal);
      setSoportes(resp.data);
      setTotal(resp.total);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
        alert(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchParams.get("cerrados")) {
      const np = new URLSearchParams(searchParams);
      np.set("cerrados", "false");
      np.set("page", "1");
      setSearchParams(np);
    } else {
      load();
    }
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line
  }, [searchParams]);

  // Filtros
  const handleFilter = () => {
    const np = new URLSearchParams();
    if (localSearch) np.set("search", localSearch);
    if (localFechaInicio) np.set("fechaInicio", localFechaInicio);
    if (localFechaFin) np.set("fechaFin", localFechaFin);
    if (localImporteDesde)
      np.set("importeDesde", cleanNumberString(localImporteDesde));
    if (localImporteHasta)
      np.set("importeHasta", cleanNumberString(localImporteHasta));
    if ((userRole === "admin" || userRole === "tecnico") && localAutor) {
      np.set("autor", localAutor);
    }
    if ((userRole === "admin" || userRole === "tecnico") && localAsignado) {
      np.set("asignado", localAsignado);
    }
    if (
      (userRole === "admin" || userRole === "tecnico") &&
      localNoRespondidos
    ) {
      np.set("noRespondidos", "true");
    }
    np.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    np.set("page", "1");
    setSearchParams(np);
  };
  const handleReset = () => {
    setLocalSearch("");
    setLocalFechaInicio("");
    setLocalFechaFin("");
    setLocalImporteDesde("");
    setLocalImporteHasta("");
    setLocalAutor("");
    setLocalAsignado("");
    setLocalNoRespondidos(false);
    const np = new URLSearchParams();
    np.set("cerrados", viewMode === "cerrados" ? "true" : "false");
    np.set("page", "1");
    setSearchParams(np);
  };

  // Filtra ocultos para cliente
  let mostrados = soportes || [];
  if (userRole === "cliente") {
    mostrados = mostrados.filter(
      (s) => !s.estado_soporte || s.estado_soporte.toLowerCase() !== "oculto"
    );
  }
  // Orden visual
  const sorted = [...mostrados].sort((a, b) => {
    const pa = getEmisorPriority(a, userRole || ""),
      pb = getEmisorPriority(b, userRole || "");
    if (pa !== pb) return pa - pb;
    const da = a.ultima_fecha_msg ? new Date(a.ultima_fecha_msg).getTime() : 0;
    const db = b.ultima_fecha_msg ? new Date(b.ultima_fecha_msg).getTime() : 0;
    return db - da;
  });

  // Descarga Excel idéntica a tu ruta /export…
  const handleDownloadExcel = async () => {
    try {
      setExcelStatus("loading");
      const token = getAuthToken();
      if (!token) throw new Error("Loguéate primero");
      const url = `${window.location.protocol}//${window.location.hostname}/api/soportes-tecnicos/export`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(resp.statusText);
      const blob = await resp.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "soportes_tecnicos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      setExcelStatus("success");
      setTimeout(() => setExcelStatus("idle"), 2000);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
      setExcelStatus("idle");
    }
  };

  const renderExcelButtonContent = () => {
    if (excelStatus === "loading") {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando Excel...
        </>
      );
    }
    if (excelStatus === "success") {
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    }
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return "No hay fecha límite";

    try {
      const fecha = new Date(fechaStr);

      if (isNaN(fecha.getTime())) {
        throw new Error("Fecha inválida");
      }

      const day = fecha.getUTCDate().toString().padStart(2, "0");
      const month = (fecha.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = fecha.getUTCFullYear();
      const hours = fecha.getUTCHours().toString().padStart(2, "0");
      const minutes = fecha.getUTCMinutes().toString().padStart(2, "0");

      return `${day}/${month}/${year} a las ${hours}:${minutes}h`;
    } catch (error) {
      console.error(
        "Error formateando fecha:",
        error,
        "Fecha recibida:",
        fechaStr
      );
      return "Fecha inválida";
    }
  };


  return (
    <div className="container-concursos">
      <h2>Soportes Técnicos</h2>

      {/* Tabs abiertos/cerrados */}
      <div style={{ marginBottom: "1rem" }}>
        <button
          onClick={() => {
            setViewMode("abiertos");
            updateCerradosParam("abiertos");
          }}
          style={{
            marginRight: "1rem",
            backgroundColor: viewMode === "abiertos" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Soportes Abiertos
        </button>
        <button
          onClick={() => {
            setViewMode("cerrados");
            updateCerradosParam("cerrados");
          }}
          style={{
            backgroundColor: viewMode === "cerrados" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Cerrados
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros">
        <input
          type="text"
          className="form-control busqueda"
          placeholder="Buscar..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFilter();
            }
          }}
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por cliente..."
            value={localAutor}
            onChange={(e) => setLocalAutor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}
        {(userRole === "admin" || userRole === "tecnico") && (
          <input
            type="text"
            className="form-control busqueda"
            placeholder="Filtrar por persona asignada..."
            value={localAsignado}
            onChange={(e) => setLocalAsignado(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFilter();
              }
            }}
          />
        )}
        <AmountDropdown
          desde={localImporteDesde}
          hasta={localImporteHasta}
          onChange={(d, h) => {
            setLocalImporteDesde(d);
            setLocalImporteHasta(h);
          }}
          placeholder="Importe"
        />
        <DatePicker
          selected={localFechaInicio ? new Date(localFechaInicio) : undefined}
          onChange={(dates: [Date | null, Date | null]) => {
            const [s, e] = dates;
            if (s) {
              const ls = new Date(s.getTime() - s.getTimezoneOffset() * 60000)
                .toISOString()
                .split("T")[0];
              setLocalFechaInicio(ls);
            } else setLocalFechaInicio("");
            if (e) {
              const le = new Date(e.getTime() - e.getTimezoneOffset() * 60000)
                .toISOString()
                .split("T")[0];
              setLocalFechaFin(le);
            } else setLocalFechaFin("");
          }}
          startDate={localFechaInicio ? new Date(localFechaInicio) : undefined}
          endDate={localFechaFin ? new Date(localFechaFin) : undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Fecha alta"
        />
        {(userRole === "admin" || userRole === "tecnico") && (
          <div className="checkbox-filtro">
            <label>
              <input
                type="checkbox"
                checked={localNoRespondidos}
                onChange={(e) => setLocalNoRespondidos(e.target.checked)}
              />
              No respondidos
            </label>
          </div>
        )}
        <button
          onClick={handleFilter}
          className="btn btn-primary"
          disabled={loading}
        >
          Filtrar
        </button>
        <button
          onClick={handleReset}
          className="btn btn-secondary"
          disabled={loading}
        >
          Resetear
        </button>
      </div>

      {/* Excel (solo admin/tecnico) */}
      {(userRole === "admin" || userRole === "tecnico") && (
        <div style={{ margin: "1rem 0" }}>
          <button
            onClick={handleDownloadExcel}
            className="btn btn-success"
            disabled={excelStatus === "loading"}
          >
            {renderExcelButtonContent()}
          </button>
        </div>
      )}

      {/* Listado */}
      <div className="listing-container" style={{ marginTop: "1rem" }}>
        {loading || !soportes ? (
          <LoadingLottie />
        ) : sorted.length === 0 ? (
          <p>No hay soportes técnicos.</p>
        ) : (
          sorted.map((s, i) => {
            // Elige estado favorito si existe, sino soporte
            const raw = s.estado_soporte_favorito ?? s.estado_soporte ?? null;
            const estadoVisible = mapEstado(raw);

            // importe
            const impTot = s.importe_total || 0,
              impEst = s.importe_estimado || 0;
            let impTxt = "N/A";
            if (impTot > 0) {
              impTxt = new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: impTot % 1 === 0 ? 0 : 2,
              }).format(impTot);
            } else if (impEst > 0) {
              impTxt = new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: impEst % 1 === 0 ? 0 : 2,
              }).format(impEst);
            }

            const boxColor = getBackgroundColor(s, userRole || "");

            return (
              <div
                key={`${s.id_concurso}-${i}`}
                className="listing-item-wrapper"
                style={{
                  boxShadow: `0 0 15px 2px ${boxColor}`,
                  marginBottom: "1rem",
                }}
              >
                <div className="listing-item">
                  <div className="info">
                    <div className="header-concurso">
                      <div className="expediente">{s.codigo_expediente}</div>
                      <div className="subheader-concurso">
                        <div
                          className={
                            "estado estado-" +
                            estadoVisible.toLowerCase().replace(/ /g, "-")
                          }
                          title="Estado del soporte"
                        >
                          {estadoVisible}
                        </div>
                        <div className="publication-date">
                          {s.fecha_publicacion
                            ? new Date(s.fecha_publicacion).toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                    </div>
                    <div className="title">{s.titulo}</div>

                    <div className="extra-info">
                      <div className="fin-plazo">
                        {s.fecha_fin_ofertas ? (
                          <>
                            <span>Hasta&nbsp;</span>
                            <span>{formatearFecha(s.fecha_fin_ofertas)}</span>
                          </>
                        ) : (
                          <span>Fecha límite no disponible</span>
                        )}
                      </div>
                    </div>

                    {(userRole === "admin" || userRole === "tecnico") &&
                      s.cliente_nombre && (
                        <div style={{ marginTop: "0.8rem", color: "#444" }}>
                          <FontAwesomeIcon
                            icon={faUser}
                            style={{ marginRight: "0.4rem" }}
                          />
                          <strong>Cliente:</strong> <em>{s.cliente_nombre}</em>
                        </div>
                      )}
                    {s.fecha_alta_soporte && (
                      <div
                        style={{
                          marginTop: ".8rem",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          style={{ marginRight: ".4rem" }}
                        />
                        <strong>Fecha alta:</strong>{" "}
                        <em>
                          {new Date(s.fecha_alta_soporte).toLocaleDateString(
                            "es-ES",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}{" "}
                          a las{" "}
                          {new Date(s.fecha_alta_soporte).toLocaleTimeString(
                            "es-ES",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </em>
                      </div>
                    )}
                  </div>
                  <div className="footer-concurso">
                    <div className="importe">{impTxt}</div>
                    <div className="actions-concurso">
                      {(userRole === "admin" || userRole === "tecnico") && (
                        <div
                          style={{
                            marginRight: "0.5rem",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            backgroundColor: "rgba(0, 123, 255, 0.15)", // color azul suave y semitransparente
                            fontSize: "0.875rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Asignado a {s.dueno_nombre ?? "nadie"}
                        </div>
                      )}
                      <Link
                        to={`/soporte-tecnico/${s.id_concurso}/${s.id_usuario}`}
                      >
                        <FontAwesomeIcon icon={faEye} title="Ver detalle" />
                      </Link>
                      {s.enlace && (
                        <a
                          href={s.enlace}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginLeft: "0.5rem" }}
                        >
                          <FontAwesomeIcon
                            icon={faLink}
                            title="Enlace oficial"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Paginación */}
      <div className="pagination-concursos" style={{ marginTop: "1rem" }}>
        <button
          onClick={() =>
            page > 1 &&
            setSearchParams((ps) => {
              ps.set("page", (page - 1).toString());
              return ps;
            })
          }
          disabled={page <= 1}
        >
          Anterior
        </button>
        <span>
          Página {page} de {maxPages}
        </span>
        <button
          onClick={() =>
            page < maxPages &&
            setSearchParams((ps) => {
              ps.set("page", (page + 1).toString());
              return ps;
            })
          }
          disabled={page >= maxPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default SoportesTecnicos;
